import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import ApiTagManagement from "../../../api/tag-managements";

const useTags = (search, filters, sorting) => {
  const [tags, setTags] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false); 
  const [isLoading, setLoading] = useState(false);
  const reqIdRef = useRef(0);
  const listRef = useRef(tags);
  useEffect(() => { listRef.current = tags; }, [tags]);

  const queryParams = useMemo(() => {
    const qp = {};
    if (search) qp.query = search;
    if (filters?.sources?.length) qp.sources = filters.sources;
    if (sorting?.sortField) qp.sortField = sorting.sortField;
    if (sorting?.sortOrder) qp.sortOrder = sorting.sortOrder;
    return qp;
  }, [search, JSON.stringify(filters), sorting?.sortField, sorting?.sortOrder]);

  const mergeUniqueById = (prev = [], incoming = []) => {
    const seen = new Set(prev.map(i => i.id));
    return prev.concat(incoming.filter(i => !seen.has(i.id)));
  };

  const loadPage = useCallback(async (nextPage, { replace = false } = {}) => {
    if (isLoading && !replace) return;
    const myReqId = ++reqIdRef.current;

    setLoading(true);
    try {
      const { data } = await ApiTagManagement.getTags({
        ...queryParams,
        page: nextPage
      });
      if (myReqId !== reqIdRef.current) return;

      const incoming = Array.isArray(data?.tags) ? data.tags : [];
      setHasMore(Boolean(data?.hasMore));
      setPage(Number.isFinite(data?.page) ? data.page : nextPage);
      setTags(prev => (replace ? incoming : mergeUniqueById(prev, incoming)));
    } catch (err) {
      console.log("Tags fetch error", err);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [queryParams]);

  const fetchNext = useCallback(() => {
    if (!hasMore || isLoading || page === 0) return;
    return loadPage(page + 1);
  }, [hasMore, isLoading, loadPage, page]);

  const resetAndFetch = useCallback(async () => {
    setTags([]);
    setPage(0);
    setHasMore(false);
    await loadPage(1, { replace: true });
  }, [loadPage]);

  useEffect(() => {
    resetAndFetch();
  }, [resetAndFetch, queryParams]);

  return { tags, hasMore, isLoading, fetchNext, resetAndFetch };
};

export default useTags;